import { ISignColorOption } from "../interfaces/ISignColorOption";
import {
  ISignForm,
  SignMount,
  MountingPoint,
  Reflex,
  SignColor,
  SignColorCode,
} from "../interfaces/ISignForm";
import parking from "../symbols/p_symbol.svg";
import parkingTimer from "../symbols/parking_timer_white.svg";
import parkingForbidden from "../symbols/parking_forbidden.svg";
import stopForbidden from "../symbols/stop_forbidden.svg";
import parkingSymbolSign from "../img/parking_sign_2.svg";
import warningSign from "../img/warning_sign.svg";
import forbiddenSign from "../img/forbidden_sign.svg";
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayUpdate,
  UseFormGetValues,
  UseFormReturn,
  UseFormSetValue,
} from "react-hook-form";
import { ISignText } from "../interfaces/ISignText";
import { ISignRequest, ITextRequest } from "../interfaces/ISignRequest";
import { IOrderStatus } from "../interfaces/IOrderStatus";
import { ISignGenerate } from "../interfaces/ISignGenerate";
import { ISymbol } from "../interfaces/ISymbol";
import { ITextFieldErrorText } from "../interfaces/ITextFieldErrorText";
import "../index.css";
import { ISignCard } from "../interfaces/ISignCard";
import { TFunction } from "react-i18next";
export const calculateScaledDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxDimension: number,
) => {
  const scaleFactor = Math.min(
    maxDimension / originalWidth,
    maxDimension / originalHeight,
  );

  return {
    width: originalWidth * scaleFactor,
    height: originalHeight * scaleFactor,
    scaleFactor,
  };
};

export const generateSignFromTemplate = (template: any): ISignForm => {
  const sign: ISignForm = {
    quantity: 1,
    quantityPrice: 0,
    totalPrice: 0,
    graffitiProtection: false,
    mount: null,
    mountingPoint: null,
    mountTotal: null,
    needsMounting: null,
    poleDimension: null,
    height: template.height,
    width: template.width,
    primaryColor: template.primaryColor,
    secondaryColor: template.secondaryColor,
    primaryColorCode: template.primaryColorCode,
    secondaryColorCode: template.secondaryColorCode,
    reflex: template.reflex,
    symbol: template?.symbol ?? null,
    texts: template.texts,
    customer: {
      companyName: "",
      name: "",
      email: "",
      phone: "",
    },
  };
  return sign;
};

export const onBackBtnClick = (
  orderStatus: IOrderStatus,
  step: number,
  setOrderStatus: React.Dispatch<React.SetStateAction<IOrderStatus>>,
  setGenerateSign: React.Dispatch<React.SetStateAction<ISignGenerate>>,
  setCheckoutModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setStep: React.Dispatch<React.SetStateAction<number>>,
) => {
  if (orderStatus?.orderSent) {
    setOrderStatus({ orderSent: false, error: false, message: "" });
    setGenerateSign({ generate: false, defaultValues: null });
    setCheckoutModalOpen(false);
  } else if (orderStatus.error) {
    setCheckoutModalOpen(false);
    setOrderStatus({ orderSent: false, error: false, message: "" });
    setStep(step - 1);
  } else if (step === 1) {
    setCheckoutModalOpen(false);
  } else {
    setStep(step - 1);
  }
};

export const onStepBtnClick = async (
  signForm: UseFormReturn<ISignForm, any, ISignForm>,
  orderStatus: IOrderStatus,
  step: number,
  setOrderStatus: React.Dispatch<React.SetStateAction<IOrderStatus>>,
  setGenerateSign: React.Dispatch<React.SetStateAction<ISignGenerate>>,
  setCheckoutModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setStep: React.Dispatch<React.SetStateAction<number>>,
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>,
) => {
  if (step === 1) {
    const valid = await signForm.trigger("mountingPoint");
    if (!valid) return;
    setStep(step + 1);
  } else if (orderStatus?.orderSent) {
    setOrderStatus({ orderSent: false, error: false, message: "" });
    setGenerateSign({ generate: false, defaultValues: null });
    setCheckoutModalOpen(false);
  } else {
    onSubmit();
  }
};

export const setSignColor = (
  cube: ISignColorOption,
  setValue: UseFormSetValue<ISignForm>,
) => {
  setValue("primaryColorCode", cube.primaryColorCode);
  setValue("secondaryColorCode", cube.secondaryColorCode);
  setValue("primaryColor", cube.primaryColor);
  setValue("secondaryColor", cube.secondaryColor);
  setValue("symbol", null);
};

export const addTextRow = (
  fields: FieldArrayWithId<ISignForm, "texts", "id">[],
  primaryColor: SignColor,
  append: UseFieldArrayAppend<ISignForm, "texts">,
  getValues: UseFormGetValues<ISignForm>,
) => {
  const textRows = getValues("texts");
  const prevFontSize =
    textRows && textRows.length > 0 && textRows[textRows.length - 1]?.fontSize
      ? textRows[textRows.length - 1].fontSize
      : 60;
  const defaultTextRow: ISignText = {
    text: "",
    fontSize: prevFontSize,
    color:
      primaryColor === SignColor.YELLOW
        ? SignColorCode.BLACK
        : SignColorCode.WHITE,
    row: fields.length + 1,
  };
  append(defaultTextRow);
};

export const updateRowNumber = (
  fields: FieldArrayWithId<ISignForm, "texts", "id">[],
  update: UseFieldArrayUpdate<ISignForm, "texts">,
) => {
  fields.forEach((field, index) => {
    if (field.row !== index + 1) {
      update(index, { ...field, row: index + 1 });
    }
  });
};

export const mapToSignRequest = (form: ISignForm): ISignRequest => {
  const texts: ITextRequest[] =
    form?.texts?.filter((x) => x.text !== "").length > 0
      ? form?.texts?.map((text) => ({
          text: text?.text,
          fontSize: text?.fontSize,
          color: mapColorCodeToColor(text?.color),
          textOrder: text?.row,
        }))
      : null;
  const request: ISignRequest = {
    quantity: form.quantity,
    quantityPrice: form.quantityPrice,
    totalPrice: form.totalPrice,
    graffitiProtection: form.graffitiProtection,
    mount: form.mount,
    poleDimension: form.poleDimension,
    mountingPoint: form?.mountingPoint,
    mountTotal: form.mountTotal,
    needsMounting: form.needsMounting,
    width: form.width,
    height: form.height,
    primaryColor: form.primaryColor,
    secondaryColor: form.secondaryColor,
    reflex: form.reflex,
    texts: texts,
    symbolText: form?.symbol?.name ?? null,
    symbolWidth: form?.symbol?.width ?? null,
    symbolHeight: form?.symbol?.height ?? null,
    symbolPlacement: form?.symbolPlacement ?? null,
    customer: form.customer,
  };
  return request;
};

export const mapColorCodeToColor = (
  colorCode: SignColorCode,
): SignColor | null => {
  switch (colorCode) {
    case SignColorCode.YELLOW:
      return SignColor.YELLOW;
    case SignColorCode.RED:
      return SignColor.RED;
    case SignColorCode.BLUE:
      return SignColor.BLUE;
    case SignColorCode.WHITE:
      return SignColor.WHITE;
    case SignColorCode.BLACK:
      return SignColor.BLACK;
    default:
      return null;
  }
};

export const mapColorCodeToTranslatedColorText = (
  colorCode: SignColorCode,
  t: TFunction<"translation", undefined>,
): string => {
  switch (colorCode) {
    case SignColorCode.YELLOW:
      return t("color.yellow");
    case SignColorCode.RED:
      return t("color.red");
    case SignColorCode.BLUE:
      return t("color.blue");
    case SignColorCode.WHITE:
      return t("color.white");
    case SignColorCode.BLACK:
      return t("color.black");
    default:
      return null;
  }
};

export const signTemplates = (): ISignForm[] => {
  return [
    {
      height: 250,
      width: 550,
      primaryColor: SignColor.YELLOW,
      secondaryColor: SignColor.RED,
      primaryColorCode: SignColorCode.YELLOW,
      secondaryColorCode: SignColorCode.RED,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      texts: [
        {
          text: "Förbud/varning",
          fontSize: 60,
          color: SignColorCode.BLACK,
          row: 1,
        },
      ],
    },
    {
      height: 200,
      width: 400,
      primaryColor: SignColor.YELLOW,
      secondaryColor: SignColor.RED,
      primaryColorCode: SignColorCode.YELLOW,
      secondaryColorCode: SignColorCode.RED,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      symbol: {
        symbol: symbols().find((x) => x.name === "Stoppförbud").symbol,
        name: "Stoppförbud",
        width: 120,
        height: 120,
      },
      texts: [
        {
          text: "Last-",
          fontSize: 60,
          color: SignColorCode.BLACK,
          row: 1,
        },
        {
          text: "plats",
          fontSize: 60,
          color: SignColorCode.BLACK,
          row: 1,
        },
      ],
    },
    {
      height: 200,
      width: 400,
      primaryColor: SignColor.YELLOW,
      secondaryColor: SignColor.RED,
      primaryColorCode: SignColorCode.YELLOW,
      secondaryColorCode: SignColorCode.RED,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      texts: [
        {
          text: "Livsfarlig",
          fontSize: 60,
          color: SignColorCode.BLACK,
          row: 1,
        },
        {
          text: "ledning",
          fontSize: 60,
          color: SignColorCode.BLACK,
          row: 2,
        },
      ],
    },
    {
      height: 250,
      width: 500,
      primaryColor: SignColor.YELLOW,
      secondaryColor: SignColor.RED,
      primaryColorCode: SignColorCode.YELLOW,
      secondaryColorCode: SignColorCode.RED,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      symbol: {
        symbol: symbols().find((x) => x.name === "Parkeringsförbud").symbol,
        name: "Parkeringsförbud",
        width: 120,
        height: 120,
      },
      texts: [
        {
          text: "Parkering",
          fontSize: 60,
          color: SignColorCode.BLACK,
          row: 1,
        },
        {
          text: "förbjuden",
          fontSize: 60,
          color: SignColorCode.BLACK,
          row: 2,
        },
      ],
    },
    {
      height: 250,
      width: 600,
      primaryColor: SignColor.BLUE,
      secondaryColor: SignColor.WHITE,
      primaryColorCode: SignColorCode.BLUE,
      secondaryColorCode: SignColorCode.WHITE,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      texts: [
        {
          text: "Påbud/anvisning",
          fontSize: 60,
          color: SignColorCode.WHITE,
          row: 1,
        },
      ],
    },
    {
      height: 200,
      width: 400,
      primaryColor: SignColor.BLUE,
      secondaryColor: SignColor.WHITE,
      primaryColorCode: SignColorCode.BLUE,
      secondaryColorCode: SignColorCode.WHITE,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      texts: [
        {
          text: "Parkering",
          fontSize: 60,
          color: SignColorCode.WHITE,
          row: 1,
        },
        {
          text: "tillåten",
          fontSize: 60,
          color: SignColorCode.WHITE,
          row: 2,
        },
      ],
    },
    {
      height: 200,
      width: 400,
      primaryColor: SignColor.BLUE,
      secondaryColor: SignColor.WHITE,
      primaryColorCode: SignColorCode.BLUE,
      secondaryColorCode: SignColorCode.WHITE,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      symbol: {
        symbol: symbols().find((x) => x.name === "Parkeringsskiva").symbol,
        name: "Parkeringsskiva",
        width: 100,
        height: 150,
      },
      texts: [
        { text: "2 tim", fontSize: 60, color: SignColorCode.WHITE, row: 1 },
        { text: "8 - 18", fontSize: 60, color: SignColorCode.WHITE, row: 2 },
      ],
    },
    {
      height: 200,
      width: 450,
      primaryColor: SignColor.BLUE,
      secondaryColor: SignColor.WHITE,
      primaryColorCode: SignColorCode.BLUE,
      secondaryColorCode: SignColorCode.WHITE,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      texts: [
        {
          text: "Använd",
          fontSize: 60,
          color: SignColorCode.WHITE,
          row: 1,
        },
        {
          text: "gasmask",
          fontSize: 60,
          color: SignColorCode.WHITE,
          row: 2,
        },
      ],
    },
  ];
};

export const gettingStartedTemplates = (): ISignForm[] => {
  return [
    {
      height: 200,
      width: 400,
      primaryColor: SignColor.BLUE,
      secondaryColor: SignColor.WHITE,
      primaryColorCode: SignColorCode.BLUE,
      secondaryColorCode: SignColorCode.WHITE,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "BRF Hejsan",
        name: "Kalle Andersson",
        email: "kalle@kalle.se",
        phone: "123456789",
      },
      texts: [
        {
          text: "Egen",
          fontSize: 60,
          color: SignColorCode.WHITE,
          row: 1,
        },
        {
          text: "text",
          fontSize: 60,
          color: SignColorCode.WHITE,
          row: 2,
        },
      ],
    },
    {
      height: 300,
      width: 600,
      primaryColor: SignColor.YELLOW,
      secondaryColor: SignColor.RED,
      primaryColorCode: SignColorCode.YELLOW,
      secondaryColorCode: SignColorCode.RED,
      reflex: Reflex.NORMAL,
      needsMounting: false,
      poleDimension: 60,
      mount: SignMount.POLE,
      mountingPoint: null,
      mountTotal: 2,
      graffitiProtection: false,
      quantity: 1,
      quantityPrice: 0,
      totalPrice: 0,
      customer: {
        companyName: "",
        name: "",
        email: "",
        phone: "",
      },
      texts: [
        {
          text: "Egen text",
          fontSize: 60,
          color: SignColorCode.BLACK,
          row: 1,
        },
      ],
    },
  ];
};

export const signExamples = (): ISignCard[] => {
  return [
    {
      signSymbol: parkingSymbolSign,
      signSymbolAlt: "Parkeringsskylt",
      header: "Tilläggstavla blå/vit",
      subText: "Påbud/anvisning",
      footerText: "Från 325kr",
      sign: gettingStartedTemplates()[0],
    },
    {
      signSymbol: warningSign,
      signSymbolAlt: "Varning för annan fara",
      header: "Tilläggstavla gul/röd",
      subText: "Varning",
      footerText: "Från 325kr",
      sign: gettingStartedTemplates()[1],
    },
    {
      signSymbol: forbiddenSign,
      signSymbolAlt: "Förbud mot infart med fordon",
      header: "Tilläggstavla gul/röd",
      subText: "Förbud/uppmaning",
      footerText: "Från 325kr",
      sign: gettingStartedTemplates()[1],
    },
  ];
};

export const sizeOptions = () => {
  return [
    {
      key: "400X200",
      text: "Bredd: 400(mm), Höjd: 200(mm)",
      height: 200,
      width: 400,
    },
    {
      key: "400X300",
      text: "Bredd: 400(mm), Höjd: 300(mm)",
      height: 300,
      width: 400,
    },
    {
      key: "400X400",
      text: "Bredd: 400(mm), Höjd: 400(mm)",
      height: 400,
      width: 400,
    },
    {
      key: "500X200",
      text: "Bredd: 500(mm), Höjd: 200(mm)",
      height: 200,
      width: 500,
    },
    {
      key: "500X300",
      text: "Bredd: 500(mm), Höjd: 300(mm)",
      height: 300,
      width: 500,
    },
    {
      key: "500X400",
      text: "Bredd: 500(mm), Höjd: 400(mm)",
      height: 400,
      width: 500,
    },
    {
      key: "560X200",
      text: "Bredd: 560(mm), Höjd: 200(mm)",
      height: 200,
      width: 560,
    },
    {
      key: "560X400",
      text: "Bredd: 560(mm), Höjd: 400(mm)",
      height: 400,
      width: 560,
    },
    {
      key: "600X200",
      text: "Bredd: 600(mm), Höjd: 200(mm)",
      height: 200,
      width: 600,
    },
    {
      key: "600X300",
      text: "Bredd: 600(mm), Höjd: 300(mm)",
      height: 300,
      width: 600,
    },
  ];
};

export const fontSizeOptions = () => {
  return [
    { key: 60, text: "60(mm)", value: 60 },
    { key: 55, text: "55(mm)", value: 55 },
    { key: 50, text: "50(mm)", value: 50 },
    { key: 45, text: "45(mm)", value: 45 },
    { key: 40, text: "40(mm)", value: 40 },
    { key: 35, text: "35(mm)", value: 35 },
  ];
};


export const fontColorOptions = () => {
  return [
    { key: "White", text: "Vit", value: SignColorCode.WHITE },
    { key: "Red", text: "Röd", value: SignColorCode.RED },
    { key: "Black", text: "Svart", value: SignColorCode.BLACK },
  ];
};

export const signColorOptions = (): ISignColorOption[] => {
  return [
    {
      primaryColorCode: SignColorCode.YELLOW,
      secondaryColorCode: SignColorCode.RED,
      primaryColor: SignColor.YELLOW,
      secondaryColor: SignColor.RED,
      key: 0,
    },
    {
      primaryColorCode: SignColorCode.BLUE,
      secondaryColorCode: SignColorCode.WHITE,
      primaryColor: SignColor.BLUE,
      secondaryColor: SignColor.WHITE,
      key: 1,
    },
  ];
};

export const mountOptions = () => {
  return [
    { key: 0, text: "Enkelklammer", value: MountingPoint.SINGLE },
    { key: 1, text: "Dubbelklammer", value: MountingPoint.DOUBLE },
  ];
};

export const poleDimensionOptions = () => {
  return [
    { key: 0, text: "48(mm)", value: 48 },
    { key: 1, text: "60(mm)", value: 60 },
    { key: 2, text: "76(mm)", value: 76 },
    { key: 3, text: "90(mm)", value: 90 },
    { key: 4, text: "108(mm)", value: 108 },
    { key: 5, text: "114(mm)", value: 114 },
    { key: 6, text: "121(mm)", value: 121 },
    { key: 7, text: "133(mm)", value: 133 },
    { key: 8, text: "152(mm)", value: 152 },
    { key: 9, text: "165(mm)", value: 165 },
  ];
};

export const symbols = () => {
  return [
    { symbol: parkingTimer, name: "Parkeringsskiva" },
    { symbol: parking, name: "P-symbol" },
    { symbol: parkingForbidden, name: "Parkeringsförbud" },
    { symbol: stopForbidden, name: "Stoppförbud" },
  ];
};

export const availableTextWidth = (
  width: number,
  scaledWidth: number,
  scaleFactor: number,
  symbol?: ISymbol,
) => {
  const borderMm = width <= 600 ? 15 : width <= 1200 ? 20 : 25;
  const borderPx = borderMm * scaleFactor;

  if (symbol) {
    return scaledWidth - borderPx * 3 - symbol.width * scaleFactor;
  } else {
    return scaledWidth - borderPx * 2;
  }
};

export const availableTextHeight = (
  height: number,
  scaledHeight: number,
  scaleFactor: number,
) => {
  const borderMm = height <= 600 ? 15 : height <= 1200 ? 20 : 25;
  const borderPx = borderMm * scaleFactor;
  const margin = 1;
  return scaledHeight - (borderPx + margin) * 2;
};

export const canAddTextRow = (
  height: number,
  scaledHeight: number,
  scaleFactor: number,
  fields: FieldArrayWithId<ISignForm, "texts", "id">[],
  getValues: UseFormGetValues<ISignForm>,
) => {
  const lineHeight = 1.2;
  const texts = getValues("texts");
  const usedTextHeight = texts.reduce(
    (sum, field) => sum + (field.fontSize ?? 60) * scaleFactor * lineHeight,
    0,
  );
  const maxTextHeight = availableTextHeight(height, scaledHeight, scaleFactor);
  const nextFontSize =
    fields.length > 0 ? fields[fields.length - 1].fontSize : 60;

  const canAddRow =
    usedTextHeight + nextFontSize * scaleFactor * lineHeight <= maxTextHeight;

  return canAddRow;
};

export const fitsInAvailableTextHeight = (
  fontSize: number,
  index: number,
  height: number,
  scaledHeight: number,
  scaleFactor: number,
  getValues: UseFormGetValues<ISignForm>,
) => {
  const lineHeight = 1.2;
  const texts = getValues("texts").filter((_, i) => i !== index);

  const fontSizeMenuOption = fontSize * scaleFactor * lineHeight;

  const usedTextHeight = texts.reduce(
    (sum, field) => sum + (field.fontSize ?? 60) * scaleFactor * lineHeight,
    0,
  );
  const maxTextHeight = availableTextHeight(height, scaledHeight, scaleFactor);

  const fontSizeFits = usedTextHeight + fontSizeMenuOption <= maxTextHeight;

  return fontSizeFits;
};

export const canSelectHeight = (
  height: number,
  scaledHeight: number,
  scaleFactor: number,
  getValues: UseFormGetValues<ISignForm>,
) => {
  const lineHeight = 1.2;
  const texts = getValues("texts");

  const usedTextHeight = texts.reduce(
    (sum, field) => sum + (field.fontSize ?? 60) * scaleFactor * lineHeight,
    0,
  );
  const maxTextHeight = availableTextHeight(height, scaledHeight, scaleFactor);

  const fitsInHeight = usedTextHeight <= maxTextHeight;

  return fitsInHeight;
};

export const onSignExpand = (
  height: number,
  width: number,
  scaledWidth: number,
  scaleFactor: number,
  heightCanExpand: boolean,
  symbol: ISymbol,
  setValue: UseFormSetValue<ISignForm>,
  getValues: UseFormGetValues<ISignForm>,
) => {
  if (!heightCanExpand) {
    return false;
  }
  let newSignMeasurment: any;
  const signSizeOptions = sizeOptions();
  const canExpandWithSameWidth = signSizeOptions.some(
    (x) => x.height > height && x.width >= width,
  );
  if (canExpandWithSameWidth) {
    newSignMeasurment = signSizeOptions.find(
      (x) => x.height > height && x.width >= width,
    );
  } else {
    const nextHeightOption = signSizeOptions.find((x) => x.height > height);
    const filteredHeightOptions = signSizeOptions.filter(
      (x) => x.height === nextHeightOption.height,
    );
    const clostestWidthOption =
      filteredHeightOptions[filteredHeightOptions.length - 1];
    newSignMeasurment = clostestWidthOption;
    const textFields = getValues("texts");
    const widthTooNarrow = textFields.some((field) => {
      return (
        measureTextWidth(
          field?.text,
          field.fontSize * scaleFactor,
          scaledWidth,
        ) >
        availableTextWidth(
          newSignMeasurment.width,
          newSignMeasurment.width * scaleFactor,
          scaleFactor,
          symbol,
        )
      );
    });
    if (widthTooNarrow) {
      return false;
    }
  }
  setValue("width", newSignMeasurment.width);
  setValue("height", newSignMeasurment.height);
  return true;
};

export const measureTextWidth = (
  text: string,
  fontSize: number,
  scaledWidth: number,
  fontFamily = "Tratex",
): number => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return 0;

  ctx.font = `bold ${fontSize}px ${fontFamily}`;
  const textWidth = ctx.measureText(text).width;

  const letterSpacingPx = scaledWidth * 0.0075;
  const numberOfSpaces = text.length > 0 ? text.length - 1 : 0;

  return textWidth + numberOfSpaces * letterSpacingPx;
};

export const textFieldHasErrorMessage = (
  fieldsWithErrorMessage: ITextFieldErrorText[],
  index: number,
) => {
  return fieldsWithErrorMessage.some((x) => x.field === `texts.${index}.text`);
};
